import { FC, useState } from 'react'
import Swal from 'sweetalert2'
import { axiosClientAuth } from '../../utils/axiosClient'
import { KTIcon } from '../../../_metronic/helpers'
import { PageLink, PageTitle } from '../../../_metronic/layout/core'
import { useIntl } from 'react-intl'

const ReportingOption: FC = () => {
    const intl = useIntl()
    const [reportType, setReportType] = useState('')
    const [selectedFilter, setSelectedFilter] = useState('')
    const [fromDate, setFromDate] = useState('')
    const [toDate, setToDate] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const reportOptions = [
        { label: intl.formatMessage({ id: 'REPORTING.OPTION.HOPS_DETAILS' }), value: 'route_details' },
        { label: intl.formatMessage({ id: 'REPORTING.OPTION.IP_DETAILS' }), value: 'ip_details' },
        { label: intl.formatMessage({ id: 'REPORTING.OPTION.TICKET_DETAILS' }), value: 'tickets' },
    ]

    const filterOptions: Record<string, { label: string; value: string }[]> = {
        'route_details': [
            { label: intl.formatMessage({ id: 'REPORTING.FILTER.ALL_HOPS' }), value: 'all_routes' },
            { label: intl.formatMessage({ id: 'REPORTING.FILTER.APPROVED_HOPS' }), value: 'approved' },
            { label: intl.formatMessage({ id: 'REPORTING.FILTER.REJECTED_HOPS' }), value: 'rejected' },
            { label: intl.formatMessage({ id: 'REPORTING.FILTER.PENDING_HOPS' }), value: 'pending' },
            { label: intl.formatMessage({ id: 'REPORTING.FILTER.TMD_HOPS' }), value: 'tms' },
            { label: intl.formatMessage({ id: 'REPORTING.FILTER.OPERATOR_HOPS' }), value: 'operators' },
        ],
        'ip_details': [
            { label: intl.formatMessage({ id: 'REPORTING.FILTER.ALL_IPS' }), value: 'all_ips' },
            { label: intl.formatMessage({ id: 'REPORTING.FILTER.ENABLED_IPS' }), value: 'enabled' },
            { label: intl.formatMessage({ id: 'REPORTING.FILTER.DISABLED_IPS' }), value: 'disabled' },
            { label: intl.formatMessage({ id: 'REPORTING.FILTER.BLACKLISTED_IPS' }), value: 'blacklist' },
            { label: intl.formatMessage({ id: 'REPORTING.FILTER.WHITELISTED_IPS' }), value: 'whitelist' },
        ],
        'tickets': [
            { label: intl.formatMessage({ id: 'REPORTING.FILTER.ALL_TICKETS' }), value: 'all' },
            { label: intl.formatMessage({ id: 'REPORTING.FILTER.OPEN_TICKETS' }), value: 'open' },
            { label: intl.formatMessage({ id: 'REPORTING.FILTER.PROCESSING_TICKETS' }), value: 'processing' },
            { label: intl.formatMessage({ id: 'REPORTING.FILTER.RESOLVED_TICKETS' }), value: 'resolved' },
        ],
    }

    const clearFields = () => {
        setReportType('')
        setFromDate('')
        setToDate('')
        setSelectedFilter('')
    }

    const isSubmitDisabled = !reportType || !selectedFilter || isLoading

    const handleDownload = async () => {
        setIsLoading(true)
        try {
            const res = await axiosClientAuth.get('reports/download/', {
                params: { type: reportType, filter: selectedFilter, start_date: fromDate, end_date: toDate },
                responseType: 'blob',
            })

            const disposition: string = res.headers['content-disposition'] ?? ''
            const filenameMatch = disposition.match(/filename="([^"]+)"/)
            const filename = filenameMatch ? filenameMatch[1] : `${reportType}_report.csv`

            const url = window.URL.createObjectURL(new Blob([res.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', filename)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)

            clearFields()
        } catch (error) {
            console.error('Error downloading report:', error)
            Swal.fire(
                intl.formatMessage({ id: 'COMMON.ALERTS.FAILED' }),
                intl.formatMessage({ id: 'REPORTING.MESSAGES.DOWNLOAD_FAILED' }),
                'error'
            )
        } finally {
            setIsLoading(false)
        }
    }

    const openDatePicker = (e: React.MouseEvent<HTMLInputElement>) => {
        const input = e.target as HTMLInputElement & { showPicker?: () => void }
        input.showPicker?.()
    }

    return (
        <div className="card w-50 mx-auto">
            <div className="card-body">
                <div className="mb-7">
                    <span className="text-gray-900 fs-h6 fw-bold d-inline-block mb-1">
                        {intl.formatMessage({ id: 'REPORTING.LABEL.REPORT_TYPE' })}
                    </span>
                    <select
                        className="form-select"
                        value={reportType}
                        onChange={(e) => { setReportType(e.target.value); setSelectedFilter('') }}
                    >
                        <option value="" disabled>
                            {intl.formatMessage({ id: 'REPORTING.PLACEHOLDER.REPORT_TYPE' })}
                        </option>
                        {reportOptions.map((item, index) => (
                            <option key={index} value={item.value}>
                                {item.label}
                            </option>
                        ))}
                    </select>
                </div>

                {reportType && (
                    <div className="mb-7">
                        <span className="text-gray-900 fs-h6 fw-bold d-inline-block mb-1">
                            {intl.formatMessage({ id: 'REPORTING.LABEL.FILTER_TYPE' })}
                        </span>
                        <select
                            className="form-select"
                            value={selectedFilter}
                            onChange={(e) => setSelectedFilter(e.target.value)}
                            aria-label="Select filter type"
                        >
                            <option value="" disabled>
                                {intl.formatMessage({ id: 'REPORTING.PLACEHOLDER.FILTER_TYPE' })}
                            </option>
                            {filterOptions[reportType].map((filter) => (
                                <option key={filter.value} value={filter.value}>
                                    {filter.label}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="mb-7">
                    <span className="text-gray-900 fs-h6 fw-bold d-inline-block mb-1">
                        {intl.formatMessage({ id: 'REPORTING.LABEL.FROM_DATE' })}
                    </span>
                    <input
                        type="date"
                        className="form-control"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        onClick={openDatePicker}
                        max={new Date().toISOString().split('T')[0]}
                    />
                </div>

                <div className="mb-6">
                    <span className="text-gray-900 fs-h6 fw-bold d-inline-block mb-1">
                        {intl.formatMessage({ id: 'REPORTING.LABEL.TO_DATE' })}
                    </span>
                    <input
                        type="date"
                        className="form-control"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        onClick={openDatePicker}
                        max={new Date().toISOString().split('T')[0]}
                    />
                </div>

                <button
                    onClick={handleDownload}
                    disabled={isSubmitDisabled}
                    className="btn btn-primary mb-4 d-flex align-items-center gap-1 mx-auto"
                >
                    <KTIcon iconName="file-down" className="text-white fs-4" iconType="outline" />
                    {isLoading
                        ? intl.formatMessage({ id: 'REPORTING.BUTTON.DOWNLOADING' })
                        : intl.formatMessage({ id: 'REPORTING.BUTTON.DOWNLOAD_CSV' })
                    }
                </button>
            </div>
        </div>
    )
}

const myBreadCrumbs: PageLink[] = [
    {
        title: 'Home',
        path: '/dashboard',
        isSeparator: false,
        isActive: false,
    },
    {
        title: '',
        path: '',
        isSeparator: true,
        isActive: false,
    },
]

const ReportingSection: FC = () => {
    const intl = useIntl()
    return (
        <>
            <PageTitle breadcrumbs={myBreadCrumbs}>{intl.formatMessage({ id: 'MENU.REPORTS' })}</PageTitle>
            <ReportingOption />
        </>
    )
}

export { ReportingSection }
